import { Directive, HostListener, Input } from '@angular/core';

import { cleanNumericalString } from '../utils/index';

export interface INumericInputDirectiveOptions {
  allowDecimal: boolean;
  allowedChars: string;
}

const DEFAULT_OPTIONS: INumericInputDirectiveOptions = {
  allowDecimal: false,
  allowedChars: '',
};

/**
 * @description A directive to allow only numeric input in text fields.
 * Allows options object (INumericInputDirectiveOptions) to be passed in.
 * Based on https://github.com/ApsisInternational/One.Site/blob/develop/src/app/shared/directives/numeric-input/numeric-input.directive.ts.
 * @example
 * <input appNumericInput>
 *
 * @example
 * <input [appNumericInput]="{ allowDecimal: false, allowedChars: '+' }"
 */
@Directive({
  selector: '[appNumericInput]',
})
export class NumericInputDirective {
  _decimalChar = '';
  _options: INumericInputDirectiveOptions;

  constructor() {
    /*
    This is the shortest and ideal way to get the decimal separator character.
    toLocaleString() with no locale param should format according to OS regional setting.
    This is known not to work properly in all browsers though - a '.' will be set in such case.
     */
    this._decimalChar = (1.1).toLocaleString().replace(/\d/g, '');
  }

  applyThousandsSeparator(value: string) {
    return cleanNumericalString(value).replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      '$1 ',
    );
  }

  get options(): INumericInputDirectiveOptions {
    return this._options;
  }

  @Input('appNumericInput')
  set options(userOptions: INumericInputDirectiveOptions) {
    this._options = { ...DEFAULT_OPTIONS, ...userOptions };
  }

  @HostListener('keydown', ['$event'])
  onKeyDown($event: KeyboardEvent) {
    const eKey = $event.key;
    const input = $event.target as HTMLInputElement;

    // eKey can be undefined when user selects from browser's suggestion list using the Enter key (Chrome Mac)
    if (!eKey || eKey.length > 1 || $event.ctrlKey || $event.metaKey) {
      return;
    }

    // allow optional characters
    if (this.options.allowedChars.indexOf(eKey) > -1) {
      return;
    }

    // handle key that are not single digits
    if (!eKey.match(/^\d$/)) {
      if (eKey === this._decimalChar) {
        // prevent dupe decimals
        const hasDecimalRegexp = new RegExp('\\' + this._decimalChar);
        const hasDecimal = input.value.match(hasDecimalRegexp);

        if (this.options.allowDecimal === false || hasDecimal) {
          $event.preventDefault();
        }
      } else {
        $event.preventDefault();
      }
    }
  }

  @HostListener('keyup', ['$event'])
  onKeyUp($event: KeyboardEvent) {
    const input = $event.target as HTMLInputElement;
    input.value = this.applyThousandsSeparator(input.value);
  }

  @HostListener('paste', ['$event'])
  onPaste($event: Event) {
    setTimeout(() => {
      const input = $event.target as HTMLInputElement;
      // upon paste the allowed characters should pass through just as while typing
      const regexp = new RegExp(`[^\\d${this.options.allowedChars}]`, 'g');
      input.value = this.applyThousandsSeparator(
        input.value.replace(regexp, ''),
      );
    });
  }
}
